import React, { useCallback } from 'react'
import { ArrowCaret, MenuNewWindow } from '@fs/zion-icon'
import { ListItem } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  getPersonName,
  getRelationshipToPrimaryPersonText,
  useSlsStateContext,
  useSlsHighlightsContext,
  getRelationshipValue,
  getPrimaryRelationshipText,
} from '@fs/zion-sls'
import {
  getIconByAttachmentStatus,
  getIconBackgroundColor,
  getIconTestId,
} from '../../../../utils/personAttachmentDisplay.utils'
import { usePersonAttachmentData } from '../../../../views/indexing/hooks/usePersonAttachmentData'
import useGroupSectionsRework from '../hooks/useGroupSectionsRework'

/**
 * PersonDisplay
 *
 * Renders a single person as a list item with attachment status icon, name, relationship label,
 * and interaction handlers for selection and highlight hovering.
 *
 * Features:
 * - Shows attachment status via icon (attached, possible match, or no attachment)
 * - Displays person name and relationship to primary person (above the name once the group
 *   sections rework is on, below it before that)
 * - Highlights person on hover
 * - Handles click to select person
 *
 * @component
 * @param {Object} props
 * @param {Object} props.person - The person object to display
 * @param {Object} [props.record] - Optional record/household object the person belongs to
 * @param {Function} [props.onPersonSelect] - Callback when person is clicked, receives (personId, recordId)
 * @param {string} [props.overline] - Optional overline text shown above the name (e.g. "On image 7").
 * @param {Function} [props.openNewPage] - When provided, the row opens this callback instead of selecting the
 *   person, and the end element becomes an open-in-new-window action. Used for names on other images.
 * @returns {JSX.Element|null} The person list item or null if person is undefined
 */
const PersonDisplay = ({ person, record, onPersonSelect = () => {}, overline, openNewPage }) => {
  const [t] = useTranslation()
  const groupSectionsRework = useGroupSectionsRework()
  const { types } = useSlsStateContext()
  const { hoverHighlights, unHover } = useSlsHighlightsContext()

  const { attachment, match } = usePersonAttachmentData(person?.id)

  const personName = getPersonName({ person, justOneName: true })
  const relationshipToPrimary = getRelationshipValue(person, types, record) || ''
  const relationshipOrPrimaryLabel = getRelationshipToPrimaryPersonText(person, types, record, t) || ''
  const relationshipLabel = getPrimaryRelationshipText(relationshipOrPrimaryLabel, relationshipToPrimary)
  // Always show icon based on attachment status
  const IconComponent = getIconByAttachmentStatus(attachment, match)
  const iconBackground = getIconBackgroundColor(attachment, match)
  const iconTestId = getIconTestId(attachment, match)
  const startElement = <ListItem.Icon Icon={IconComponent} background={iconBackground} data-testid={iconTestId} />

  const handleClick = useCallback(() => {
    if (openNewPage) {
      openNewPage()
      return
    }
    onPersonSelect(person?.id, record?.id)
  }, [openNewPage, onPersonSelect, person?.id, record?.id])

  // Only the person's name highlights react to the hover. Record outlines are always on, so there is
  // nothing for the hover to stage on the record - see SideSheet.js.
  const handleMouseEnter = useCallback(() => {
    hoverHighlights({ elementIds: [person?.id] })
  }, [hoverHighlights, person?.id])

  const handleMouseLeave = useCallback(() => {
    unHover()
  }, [unHover])

  const endelementCaretButtonTitle = openNewPage
    ? t('person-display.button.open-new-tab', 'Open {personName} in a new tab', { personName })
    : `${t('person-display.button.person-select', 'Click to view')} ${personName}`

  // Names on other images already use `overline` for "On image 7", so their relationship stays
  // below the name. Zion renders the overline as visible text inside the row's role="button", which
  // keeps the relationship part of the computed accessible name.
  const relationshipInOverline = groupSectionsRework && !overline

  const endElement = (
    <ListItem.IconButton
      Icon={openNewPage ? MenuNewWindow : ArrowCaret}
      color="gray"
      title={endelementCaretButtonTitle}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )

  if (!person) return null

  return (
    <ListItem
      data-testid={personName}
      key={person?.id}
      overline={relationshipInOverline ? relationshipLabel : overline}
      primaryText={personName}
      secondaryText={relationshipInOverline ? undefined : relationshipLabel}
      emphasized={!!person?.primary}
      startElement={startElement}
      endElement={endElement}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )
}

export default PersonDisplay
